import React, { useState } from 'react';
import { WarningIcon, ErrorIcon, SuccessIcon, TipIcon, AnalyticsIcon, AccentIcon, GoalIcon, LaunchIcon } from '../../../icons';
import { AssessmentLevel, CompleteAssessment, DISCProfile, WorkstyleProfile, CapabilityDecision, ExtendedCapabilityDecision, CapabilityDomain } from './types';
import { Goal } from '../types';
import DISCAssessment from './DISCAssessment';
import WorkstyleAssessment from './WorkstyleAssessment';
import CapabilityWizard from './CapabilityWizard';
import ExtendedSkillsWizard from './ExtendedSkillsWizard';
import PostAssessmentMilestonesReview from './PostAssessmentMilestonesReview';
import { getLatestAssessment } from './capabilityStorage.service';

interface AssessmentFlowProps {
  goal: Goal;
  ownerId: string;
  onComplete: (assessment: CompleteAssessment) => void;
  onCancel: () => void;
}

type FlowStep = 
  | 'choice'              // Keuzemenu
  | 'skills'              // Skill vragen (CapabilityWizard)
  | 'disc'                // DISC test
  | 'workstyle'           // Werkstijl test
  | 'extended'            // Extended Skills (uitgebreid)
  | 'milestones_review'   // Mijlpalen review & bewerking (NIEUW!)
  | 'result'              // Resultaat pagina
  | 'skip_warning';       // Waarschuwing bij overslaan

/**
 * Assessment Flow Component
 * 
 * Orkestreert de volledige assessment flow:
 * 1. Keuzemenu (Snel/Uitgebreid/Overslaan)
 * 2. DISC Test (optioneel)
 * 3. Werkstijl Test (optioneel)
 * 4. Extended Skills (optioneel)
 * 5. Resultaat pagina
 */
const AssessmentFlow: React.FC<AssessmentFlowProps> = ({
  goal,
  ownerId,
  onComplete,
  onCancel
}) => {
  const [currentStep, setCurrentStep] = useState<FlowStep>('choice');
  const [selectedLevel, setSelectedLevel] = useState<AssessmentLevel | null>(null);
  
  // Collected data
  const [skillDecisions, setSkillDecisions] = useState<CapabilityDecision[]>([]);
  const [discProfile, setDiscProfile] = useState<DISCProfile | null>(null);
  const [workstyleProfile, setWorkstyleProfile] = useState<WorkstyleProfile | null>(null);
  const [extendedAnswers, setExtendedAnswers] = useState<Partial<ExtendedCapabilityDecision>[]>([]);
  const [completeAssessmentData, setCompleteAssessmentData] = useState<CompleteAssessment | null>(null);

  // Handle level selection
  const handleLevelSelect = (level: AssessmentLevel) => {
    setSelectedLevel(level);

    if (level === 'none') {
      setCurrentStep('skip_warning');
    } else if (level === 'basic') {
      // Snelle Scan: Start met skill vragen
      setCurrentStep('skills');
    } else if (level === 'full') {
      // Volledig: Start met skill vragen, daarna DISC, daarna Werkstijl
      setCurrentStep('skills');
    } else if (level === 'extended') {
      // Extended: Alle voorgaande + Extended Skills
      setCurrentStep('skills');
    }
  };

  // Handle Skills (CapabilityWizard) completion
  const handleSkillsComplete = async () => {
    console.log('🔵 [AssessmentFlow] handleSkillsComplete called, selectedLevel:', selectedLevel);
    
    // Load the just-saved skill decisions
    let loadedDecisions: CapabilityDecision[] = [];
    try {
      const assessment = await getLatestAssessment(ownerId, goal.id);
      if (assessment) {
        console.log('✅ [AssessmentFlow] Loaded assessment decisions:', assessment.decisions.length);
        loadedDecisions = assessment.decisions;
        setSkillDecisions(assessment.decisions);
      }
    } catch (err) {
      console.error('Could not load skill decisions:', err);
    }

    if (selectedLevel === 'basic') {
      // Snelle Scan: Klaar na skills
      console.log('🟢 [AssessmentFlow] Basic selected → completeAssessment');
      // ✅ FIX: Pass loaded decisions directly to avoid race condition with state update
      completeAssessment('basic', null, null, loadedDecisions);
    } else if (selectedLevel === 'full' || selectedLevel === 'extended') {
      // Volledig/Extended: Door naar DISC
      console.log('🟡 [AssessmentFlow] Full/Extended → go to DISC');
      setCurrentStep('disc');
    }
  };

  // Handle DISC completion
  const handleDISCComplete = (profile: DISCProfile) => {
    setDiscProfile(profile);
    // Next: Werkstijl test
    setCurrentStep('workstyle');
  };

  // Handle Werkstijl completion
  const handleWorkstyleComplete = (profile: WorkstyleProfile) => {
    setWorkstyleProfile(profile);
    
    if (selectedLevel === 'extended') {
      // Extended: Door naar Extended Skills
      setCurrentStep('extended');
    } else {
      // Full: Klaar na Werkstijl
      console.log('🟢 [AssessmentFlow] Full selected → completeAssessment');
      // Note: skillDecisions should be loaded by now from handleSkillsComplete
      completeAssessment('full', discProfile, profile);
    }
  };

  // Handle Extended Skills completion
  const handleExtendedSkillsComplete = (answers: Partial<ExtendedCapabilityDecision>[]) => {
    setExtendedAnswers(answers);
    // Extended assessment klaar
    console.log('🟢 [AssessmentFlow] Extended selected → completeAssessment');
    // Note: skillDecisions should be loaded by now from handleSkillsComplete
    completeAssessment('extended', discProfile, workstyleProfile);
  };

  // Complete assessment
  const completeAssessment = (
    level: AssessmentLevel,
    disc: DISCProfile | null,
    workstyle: WorkstyleProfile | null,
    decisionsOverride?: CapabilityDecision[] // ✅ FIX: Allow passing decisions directly to avoid race condition
  ) => {
    console.log('🔵 [AssessmentFlow] completeAssessment called with level:', level);
    
    // ✅ FIX: Use override if provided, otherwise use state
    const decisionsToUse = decisionsOverride || skillDecisions;
    console.log('📊 [AssessmentFlow] skillDecisions count:', decisionsToUse.length);
    
    // Merge extended answers into decisions if available
    let finalDecisions = decisionsToUse;
    if (extendedAnswers.length > 0) {
      finalDecisions = decisionsToUse.map(decision => {
        const extended = extendedAnswers.find(ext => ext.domain === decision.domain);
        return extended ? { ...decision, ...extended } : decision;
      });
    }

    const assessment: CompleteAssessment = {
      id: `assessment_${Date.now()}`,
      goalId: goal.id,
      ownerId,
      decisions: finalDecisions,
      completedAt: Date.now(),
      createdAt: Date.now(),
      assessmentLevel: level,
      discProfile: disc || undefined,
      workstyleProfile: workstyle || undefined
    };

    console.log('✅ [AssessmentFlow] Assessment created with', finalDecisions.length, 'decisions');
    console.log('🚀 [AssessmentFlow] Setting step to milestones_review');
    
    // Store assessment data and go to milestones review
    setCompleteAssessmentData(assessment);
    setCurrentStep('milestones_review');
  };

  // Skip warning confirmed
  const handleSkipConfirm = () => {
    console.log('⚠️ [AssessmentFlow] SKIP CONFIRMED - User chose to skip assessment');
    // When skipping, we don't have assessment data, so directly call onComplete
    const assessment: CompleteAssessment = {
      id: `assessment_${Date.now()}`,
      goalId: goal.id,
      ownerId,
      decisions: [],
      completedAt: Date.now(),
      createdAt: Date.now(),
      assessmentLevel: 'none'
    };
    console.log('⚠️ [AssessmentFlow] Calling onComplete with EMPTY assessment');
    onComplete(assessment);
  };

  // Render current step
  console.log('🎨 [AssessmentFlow] Rendering, currentStep:', currentStep, 'completeAssessmentData:', !!completeAssessmentData);
  
  // Handle back navigation
  const handleBackFromStep = () => {
    if (currentStep === 'skills') {
      setCurrentStep('choice');
    } else if (currentStep === 'disc') {
      setCurrentStep('skills');
    } else if (currentStep === 'workstyle') {
      setCurrentStep('disc');
    } else if (currentStep === 'extended') {
      setCurrentStep('workstyle');
    }
  };

  if (currentStep === 'skills') {
    return (
      <CapabilityWizard
        goal={goal}
        ownerId={ownerId}
        onComplete={handleSkillsComplete}
        onCancel={handleBackFromStep}
        skipSummary={true}
      />
    );
  }

  if (currentStep === 'disc') {
    return (
      <DISCAssessment
        onComplete={handleDISCComplete}
        onCancel={handleBackFromStep}
      />
    );
  }

  if (currentStep === 'workstyle') {
    return (
      <WorkstyleAssessment
        onComplete={handleWorkstyleComplete as any} // Cast needed because prop type expects sync function
        onCancel={handleBackFromStep}
      />
    );
  }

  if (currentStep === 'extended') {
    // Get domains where required=true from skillDecisions
    const requiredDomains = skillDecisions
      .filter(d => d.required)
      .map(d => d.domain) as CapabilityDomain[];

    return (
      <ExtendedSkillsWizard
        domains={requiredDomains}
        onComplete={handleExtendedSkillsComplete as any} // Cast needed because prop type expects sync function
        onCancel={handleBackFromStep}
      />
    );
  }

  if (currentStep === 'milestones_review' && completeAssessmentData) {
    return (
      <PostAssessmentMilestonesReview
        goal={goal}
        ownerId={ownerId}
        assessment={completeAssessmentData}
        onComplete={() => onComplete(completeAssessmentData)}
        onBack={() => setCurrentStep('extended')}
      />
    );
  }

  if (currentStep === 'skip_warning') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90dvh] overflow-y-auto scrollbar-always">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-400 px-6 py-4 text-white">
            <h2 className="text-xl font-bold inline-flex items-center gap-1"><WarningIcon size={20} className="inline" /> Weet je het zeker?</h2>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-gray-700">
              Als je het assessment overslaat, kunnen de gegenereerde mijlpalen en acties{' '}
              <strong>significant afwijken</strong> van wat realistisch is voor jouw situatie.
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
              <h4 className="font-semibold text-red-900"><ErrorIcon size={16} className="inline text-red-700" /> Zonder assessment:</h4>
              <ul className="space-y-1 text-sm text-red-800">
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Mijlpalen zijn algemeen, niet op jouw niveau</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>AI kent je sterke/zwakke punten niet</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Acties kunnen te moeilijk of te makkelijk zijn</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Geen inzicht in je werkstijl</span>
                </li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
              <h4 className="font-semibold text-green-900"><SuccessIcon size={16} className="inline text-green-700" /> Met volledig assessment:</h4>
              <ul className="space-y-1 text-sm text-green-800">
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Mijlpalen passen bij jouw niveau en stijl</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>AI begrijpt waar je hulp nodig hebt</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Acties zijn realistisch voor jouw situatie</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Tijdlijn houdt rekening met je werkstijl</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <TipIcon size={14} className="inline" /> <strong>Tip:</strong> Je kunt dit later alsnog invullen via je profiel, 
                maar dan moet je opnieuw sparren voor gepersonaliseerde mijlpalen.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setCurrentStep('choice')}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors"
              >
                Toch invullen (aanbevolen)
              </button>
              <button
                onClick={handleSkipConfirm}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium"
              >
                Definitief overslaan
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Choice menu
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90dvh] overflow-y-auto scrollbar-always my-4">
        {/* Header */}
        <div className="px-4 py-4 text-white" style={{ background: 'linear-gradient(135deg, #2E5F56 0%, #4A7A70 100%)' }}>
          <h1 className="text-xl font-bold mb-1 inline-flex items-center gap-1"><AnalyticsIcon size={20} className="inline" /> Capaciteiten Assessment</h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>
            Voordat we starten met sparring, kunnen we je profiel in kaart brengen. 
            Hoe meer we weten, hoe beter de mijlpalen en acties bij jou passen.
          </p>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-gray-700 text-center mb-3 text-sm">
            Kies hieronder hoe uitgebreid je dit wilt invullen:
          </p>

          {/* OPTION 1: SNEL */}
          <div className="border-2 border-[#E0DDD8] rounded-xl p-4 hover:border-[#8DA8A0] transition-colors">
            <div className="flex items-start gap-4 mb-4">
              <span className="text-4xl">⚡</span>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Snelle Scan (5-10 minuten)</h3>
                
                <div className="space-y-2 mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-700 text-sm mb-1">📝 Wat je invult:</h4>
                    <p className="text-sm text-gray-600">
                      <strong>Skills per Domein</strong> - Per domein: Ben je er goed in? Wie doet het? Hoe wil je gaps oplossen?
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm mb-1" style={{ color: '#2E5F56' }}><SuccessIcon size={14} className="inline" /> Wat je krijgt:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Mijlpalen gebaseerd op je skill-niveau</li>
                      <li>• Duidelijk wat je moet leren/uitbesteden/aannemen</li>
                      <li>• Acties per domein afgestemd op jou</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm mb-1" style={{ color: '#5A5A5A' }}><ErrorIcon size={14} className="inline" /> Wat je mist:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Geen DISC persoonlijkheidstest</li>
                      <li>• Geen werkstijl-analyse</li>
                      <li>• AI kent je karakter niet</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600">
                      <TipIcon size={14} className="inline" /> <strong>Voor wie:</strong> Je hebt weinig tijd, wilt snel starten met skill-gebaseerde mijlpalen
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleLevelSelect('basic')}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors"
                >
                  ⚡ Snelle Scan Starten →
                </button>
              </div>
            </div>
          </div>

          {/* OPTION 2: VOLLEDIG (AANBEVOLEN) */}
          <div className="border-2 rounded-xl p-4 relative" style={{ borderColor: '#3ECBB5', background: 'rgba(62,203,181,0.05)' }}>
            <div className="absolute -top-3 left-6 text-white px-4 py-1 rounded-full text-sm font-bold" style={{ background: '#3ECBB5', color: '#fff' }}>
              <AccentIcon size={14} className="inline" /> AANBEVOLEN
            </div>

            <div className="flex items-start gap-4 mb-4 mt-2">
              <GoalIcon size={32} />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Volledig Profiel (25-30 minuten)</h3>
                
                <div className="space-y-2 mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-700 text-sm mb-1">📝 Wat je invult:</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><strong>1. Skills per Domein (5-10 min)</strong> - Zelfde als Snelle Scan</p>
                      <p><strong>2. DISC Persoonlijkheid (8 min)</strong> - Ben je Rood (doener), Geel (netwerker), Groen (teamspeler) of Blauw (analist)?</p>
                      <p><strong>3. Werkstijl Test (8 min)</strong> - Werk je snel of gestructureerd? Doe je veel research of duik je direct in?</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm mb-1" style={{ color: '#2E5F56' }}><SuccessIcon size={14} className="inline" /> Wat je krijgt (alles van Snel + extra):</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <SuccessIcon size={14} className="inline" /> Alles van Snelle Scan</li>
                      <li>• <strong>+ Gepersonaliseerde tijdlijn:</strong> Aangepast aan jouw werkstijl</li>
                      <li>• <strong>+ Slimme AI:</strong> Begrijpt je persoonlijkheid en stelt gerichte vragen</li>
                      <li>• <strong>+ Betere sparring:</strong> AI past communicatiestijl aan op jouw DISC</li>
                    </ul>
                  </div>

                  <div className="rounded-lg p-3 border" style={{ background: '#F7F5F0', borderColor: '#E0DDD8' }}>
                    <p className="text-xs" style={{ color: '#5A5A5A' }}>
                      <TipIcon size={14} className="inline" /> <strong>Voor wie:</strong> Je wilt de beste resultaten en investeert graag 25 minuten 
                      voor een volledig gepersonaliseerde ervaring
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleLevelSelect('full')}
                  className="w-full px-6 py-4 text-white rounded-lg text-lg font-bold transition-all shadow-sm"
                  style={{ background: '#8DA8A0', color: '#fff' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#7A9488')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#8DA8A0')}
                >
                  <GoalIcon size={16} className="inline" /> Volledig Profiel Maken →
                </button>
              </div>
            </div>
          </div>

          {/* OPTION 3: ULTRA UITGEBREID */}
          <div className="border-2 border-[#E0DDD8] rounded-xl p-4 hover:border-[#8DA8A0] transition-colors" style={{ background: '#F7F5F0' }}>
            <div className="flex items-start gap-4 mb-4">
              <LaunchIcon size={32} />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Ultra Uitgebreid (60-90 minuten)</h3>
                
                <div className="space-y-2 mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-700 text-sm mb-1">📝 Wat je invult:</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><strong>1.-3. Alles van Volledig Profiel</strong> - Skills + DISC + Werkstijl</p>
                      <p><strong>4. Extended Skills (60 min)</strong> - 15-20 vragen per relevant domein:</p>
                      <ul className="pl-4 space-y-0.5 text-xs">
                        <li>• Huidige Situatie (wat doe je nu?)</li>
                        <li>• Concrete Vaardigheden (wat kun je?)</li>
                        <li>• Knelpunten (waar loop je tegenaan?)</li>
                        <li>• Actieplan (wat ga je doen?)</li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm mb-1" style={{ color: '#2E5F56' }}><SuccessIcon size={14} className="inline" /> Wat je krijgt (alles van Volledig + extra):</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <SuccessIcon size={14} className="inline" /> Alles van Volledig Profiel</li>
                      <li>• <strong>+ Hyper-specifieke acties:</strong> Gebaseerd op je exacte situatie</li>
                      <li>• <strong>+ Domein-specifieke strategie:</strong> Per domein een volledig actieplan</li>
                      <li>• <strong>+ Identificatie van gaps:</strong> Precies waar je hulp nodig hebt</li>
                      <li>• <strong>+ Budget/tijdlijn planning:</strong> Realistische actieplannen per domein</li>
                    </ul>
                  </div>

                  <div className="rounded-lg p-3 border" style={{ background: '#F7F5F0', borderColor: '#E0DDD8' }}>
                    <p className="text-xs" style={{ color: '#5A5A5A' }}>
                      <TipIcon size={14} className="inline" /> <strong>Voor wie:</strong> Je bent serieus en wilt een volledig op maat gemaakt actieplan. 
                      Je neemt de tijd voor 100+ vragen om perfecte afstemming te krijgen.
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleLevelSelect('extended')}
                  className="w-full px-6 py-3 text-white rounded-lg font-bold transition-all shadow-sm"
                  style={{ background: '#2E5F56', color: '#fff' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#234B43')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#2E5F56')}
                >
                  <LaunchIcon size={16} className="inline" /> Ultra Uitgebreid Starten →
                </button>
              </div>
            </div>
          </div>

          {/* OPTION 4: OVERSLAAN */}
          <div className="border-2 border-[#E0DDD8] rounded-xl p-4 hover:border-[#9A9A9A] transition-colors">
            <div className="flex items-start gap-4 mb-4">
              <span className="text-4xl">🚫</span>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Overslaan</h3>
                
                <div className="space-y-2 mb-3">
                  <div>
                    <h4 className="font-semibold text-green-700 text-sm mb-1"><SuccessIcon size={14} className="inline text-green-700" /> Wat je krijgt:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Algemene mijlpalen gebaseerd op je doel</li>
                      <li>• Standaard acties voor elk domein</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-red-700 text-sm mb-1"><ErrorIcon size={14} className="inline text-red-700" /> Wat je mist:</h4>
                    <ul className="text-sm text-red-600 space-y-1">
                      <li>• Geen match met je persoonlijkheid</li>
                      <li>• Geen rekening met je skills</li>
                      <li>• Acties kunnen te makkelijk of te moeilijk zijn</li>
                      <li>• Mogelijk irrelevante mijlpalen</li>
                    </ul>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-xs text-orange-800">
                      <WarningIcon size={14} className="inline" /> <strong>Let op:</strong> Zonder assessment kunnen resultaten significant afwijken 
                      van wat realistisch is voor jouw situatie
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleLevelSelect('none')}
                  className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Toch Overslaan
                </button>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="mt-3 bg-gray-50 rounded-xl p-3">
            <h4 className="font-semibold text-gray-900 mb-2 text-center text-sm"><AnalyticsIcon size={14} className="inline" /> Vergelijking</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 px-3 text-gray-700"></th>
                    <th className="text-center py-2 px-2 text-blue-700">Snel</th>
                    <th className="text-center py-2 px-2 text-green-700 font-bold">Volledig</th>
                    <th className="text-center py-2 px-2 text-purple-700">Ultra</th>
                    <th className="text-center py-2 px-2 text-gray-700">Skip</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-3 text-gray-700">Tijd</td>
                    <td className="text-center py-2 px-2">5-10m</td>
                    <td className="text-center py-2 px-2 font-bold">25-30m</td>
                    <td className="text-center py-2 px-2 text-purple-600">60-90m</td>
                    <td className="text-center py-2 px-2">0m</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-3 text-gray-700">Skills check</td>
                    <td className="text-center py-2 px-2 text-blue-600"><SuccessIcon size={14} /></td>
                    <td className="text-center py-2 px-2 text-green-600"><SuccessIcon size={14} /></td>
                    <td className="text-center py-2 px-2 text-purple-600"><SuccessIcon size={14} /></td>
                    <td className="text-center py-2 px-2"><ErrorIcon size={14} /></td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-3 text-gray-700">DISC</td>
                    <td className="text-center py-2 px-2"><ErrorIcon size={14} /></td>
                    <td className="text-center py-2 px-2 text-green-600"><SuccessIcon size={14} /></td>
                    <td className="text-center py-2 px-2 text-purple-600"><SuccessIcon size={14} /></td>
                    <td className="text-center py-2 px-2"><ErrorIcon size={14} /></td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-3 text-gray-700">Werkstijl</td>
                    <td className="text-center py-2 px-2"><ErrorIcon size={14} /></td>
                    <td className="text-center py-2 px-2 text-green-600"><SuccessIcon size={14} /></td>
                    <td className="text-center py-2 px-2 text-purple-600"><SuccessIcon size={14} /></td>
                    <td className="text-center py-2 px-2"><ErrorIcon size={14} /></td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-3 text-gray-700">Extended Skills</td>
                    <td className="text-center py-2 px-2"><ErrorIcon size={14} /></td>
                    <td className="text-center py-2 px-2"><ErrorIcon size={14} /></td>
                    <td className="text-center py-2 px-2 text-purple-600"><SuccessIcon size={14} /></td>
                    <td className="text-center py-2 px-2"><ErrorIcon size={14} /></td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-3 text-gray-700">Detail niveau</td>
                    <td className="text-center py-2 px-2 text-xs">Basis</td>
                    <td className="text-center py-2 px-2 text-green-600 font-bold text-xs">Hoog</td>
                    <td className="text-center py-2 px-2 text-purple-600 font-bold text-xs">Max</td>
                    <td className="text-center py-2 px-2 text-xs">Geen</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center pt-2 pb-1">
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700 text-xs"
            >
              Ik wil het later beslissen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentFlow;
